import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from 'contexts/AuthContext';

const FavoritesContext = createContext(null);

const storageKey = (compCd, username) => {
  const org = String(compCd || '').trim() || 'anon-tenant';
  return `hr.favorites.${org}.${username || 'guest'}`;
};

/**
 * 즐겨찾기 — screenId 기준, 사용자·기관별 localStorage 저장
 */
export const FavoritesProvider = ({ children }) => {
  const { user, compCd } = useAuth();
  const username = user?.username || user?.name || 'guest';
  const org = String(compCd || user?.compCd || '').trim() || 'anon-tenant';
  const key = storageKey(org, username);

  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : [];
      setFavorites(Array.isArray(parsed) ? parsed : []);
    } catch {
      setFavorites([]);
    }
  }, [key]);

  const persist = useCallback((next) => {
    setFavorites(next);
    try {
      localStorage.setItem(key, JSON.stringify(next));
    } catch {
      /* ignore quota */
    }
  }, [key]);

  const isFavorite = useCallback(
    (screenId) => {
      if (!screenId) return false;
      return favorites.some((f) => f.screenId === screenId);
    },
    [favorites]
  );

  const toggleFavorite = useCallback((item) => {
    const screenId = item?.screenId || item?.progId;
    if (!screenId) return;

    setFavorites((prev) => {
      const exists = prev.some((f) => f.screenId === screenId);
      const next = exists
        ? prev.filter((f) => f.screenId !== screenId)
        : [
          ...prev,
          {
            screenId,
            id: item.id || item.menuNo,
            label: item.label || item.menuNm || screenId,
            path: item.path || item.url || '',
            component: item.component || item.progId || item.componentPath || screenId,
            componentPath: item.componentPath || '',
            progId: item.progId || screenId,
            menuType: item.menuType || '',
          },
        ];
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, [key]);

  const removeFavorite = useCallback((screenId) => {
    setFavorites((prev) => {
      const next = prev.filter((f) => f.screenId !== screenId);
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, [key]);

  const value = useMemo(
    () => ({
      favorites,
      isFavorite,
      toggleFavorite,
      removeFavorite,
      persist,
    }),
    [favorites, isFavorite, toggleFavorite, removeFavorite, persist]
  );

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const ctx = useContext(FavoritesContext);
  if (!ctx) {
    throw new Error('useFavorites must be used within FavoritesProvider');
  }
  return ctx;
};

export const useFavoritesOptional = () => useContext(FavoritesContext);

export default FavoritesContext;
